import { useEffect, useMemo, useState } from "react";
import { Link, matchPath, useLocation } from "react-router";

import {
  formatAdminPermissionLabel,
  sanitizeAdminPermissionCopy,
} from "./adminPermissionCopy";
import { isForbiddenIdentityError } from "./permissionProtectedRouteHelpers";

import { adminUiCopy } from "@/copy/adminUiCopy";
import { ADMIN_ROUTES } from "@/routing/adminRoutes";
import { apiSlice } from "@/store/api/baseApi";
import { useAppSelector } from "@/store/store";
import {
  ADMIN_ACCESS_DENIED_EVENT,
  type AdminAccessDeniedEventDetail,
} from "@/utils/adminAccessDeniedEvents";
import {
  isAdminE2eBypassEnabled,
  isAdminV2FoundationsEnabled,
} from "@/utils/featureFlags";
import { VNextDialog, VNextNotice, VNextPill } from "@/vnext/primitives";
import "@/vnext/theme/vnext.css";

/**
 * Modal shown when the admin API returns an access-denied response.
 *
 * @remarks
 * The event payload should be user-safe (no internal stack traces, SQL, etc.).
 * We use it to render a small, actionable summary: which permission key(s) are
 * missing and which correlation id support can use to investigate.
 *
 * @see apps/admin/src/utils/adminAccessDeniedEvents.ts
 * @see apps/admin/src/routing/adminRoutes.tsx
 * @see docs/LOGGING.md
 * @see docs/guides/PLAIN_LANGUAGE.md
 */
interface ModalState {
  isOpen: boolean;
  signature: string | null;
  detail: AdminAccessDeniedEventDetail | null;
}

const normalizeRoutePathname = (pathname: string): string => {
  if (pathname === "/") {
    return pathname;
  }

  // Normalise trailing slashes so route matching doesn't produce different results for
  // `/foo` vs `/foo/`.
  const normalizedPathname = pathname.replace(/\/+$/, "");
  return normalizedPathname.length > 0 ? normalizedPathname : "/";
};

const resolveRoutePermissionForPathname = (pathname: string): string | null => {
  const normalizedPathname = normalizeRoutePathname(pathname);
  const permissionRoutes = ADMIN_ROUTES.filter(
    (route) => route.requiredPermission,
  );

  const chooseMostSpecificPermission = (
    routes: typeof permissionRoutes,
  ): string | null => {
    if (routes.length === 0) {
      return null;
    }

    // A more specific route match should win so we don't show a generic permission
    // when the nested route is actually stricter.
    return (
      routes.sort((a, b) => b.path.length - a.path.length)[0]
        ?.requiredPermission ?? null
    );
  };

  const exactMatches = permissionRoutes.filter(
    (route) =>
      matchPath({ path: route.path, end: true }, normalizedPathname) !== null,
  );
  const exactPermission = chooseMostSpecificPermission(exactMatches);
  if (exactPermission) {
    return exactPermission;
  }

  const prefixMatches = permissionRoutes.filter(
    (route) =>
      matchPath({ path: route.path, end: false }, normalizedPathname) !== null,
  );

  return chooseMostSpecificPermission(prefixMatches);
};

const resolveCurrentRouteRequiredPermission = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  // We resolve the route-derived permission lazily to avoid coupling this modal to router state.
  return resolveRoutePermissionForPathname(window.location.pathname);
};

const buildEventSignature = (detail: AdminAccessDeniedEventDetail): string => {
  // De-dupe repeated "access denied" events for the same failing request so we don't
  // spam the user with identical modals during retries.
  const parts = [
    detail.endpoint,
    detail.status.toString(),
    detail.requiredPermission ?? "",
    detail.correlationId ?? "",
  ];
  return parts.join("|");
};

const AdminAccessDeniedModal = () => {
  const location = useLocation();
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    signature: null,
    detail: null,
  });

  const meQuery = useAppSelector(
    apiSlice.endpoints.getAdminV2Me.select(undefined),
  );
  // CRITICAL (SLK-03 definite 403): on a permission-gated route the route
  // itself owns a definite identity denial. Do not layer "More access is
  // required" action-permission guidance on top of that one message.
  const routeOwnsIdentityDenial = Boolean(
    meQuery.isError &&
    isForbiddenIdentityError(meQuery.error) &&
    isAdminV2FoundationsEnabled() &&
    !isAdminE2eBypassEnabled() &&
    ADMIN_ROUTES.some(
      (route) =>
        route.requiredPermission &&
        matchPath({ path: route.path, end: true }, location.pathname),
    ),
  );

  useEffect(() => {
    if (routeOwnsIdentityDenial) {
      setModalState({ isOpen: false, signature: null, detail: null });
    }
  }, [routeOwnsIdentityDenial]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleAccessDeniedEvent = (event: Event) => {
      if (routeOwnsIdentityDenial) return;
      const typedEvent = event as CustomEvent<AdminAccessDeniedEventDetail>;
      const detail = typedEvent.detail;

      if (!detail.endpoint) {
        return;
      }

      const signature = buildEventSignature(detail);
      setModalState((previousState) => {
        if (previousState.signature === signature && previousState.isOpen) {
          return previousState;
        }

        return {
          isOpen: true,
          signature,
          detail,
        };
      });
    };

    window.addEventListener(ADMIN_ACCESS_DENIED_EVENT, handleAccessDeniedEvent);

    return () => {
      window.removeEventListener(
        ADMIN_ACCESS_DENIED_EVENT,
        handleAccessDeniedEvent,
      );
    };
  }, [routeOwnsIdentityDenial]);

  const closeModal = () => {
    setModalState((previousState) => ({
      ...previousState,
      isOpen: false,
    }));
  };

  const requiredPermissions = useMemo(() => {
    const currentPermissionKeys = meQuery.data?.permissionKeys ?? [];
    const resolvedPermissions = new Set<string>();
    const endpointPermission = modalState.detail?.requiredPermission;
    const routePermission = resolveCurrentRouteRequiredPermission();
    const currentPermissions = new Set(currentPermissionKeys);

    // The endpoint may report a specific permission key, while the route table may require a
    // broader one. We display the union (minus already-granted keys) so the admin can request
    // the minimum missing access without guessing.
    if (endpointPermission) {
      resolvedPermissions.add(endpointPermission);
    }

    if (routePermission) {
      resolvedPermissions.add(routePermission);
    }

    return Array.from(resolvedPermissions)
      .filter((permissionKey) => !currentPermissions.has(permissionKey))
      .sort((a, b) => a.localeCompare(b));
  }, [meQuery.data?.permissionKeys, modalState.detail?.requiredPermission]);

  if (!modalState.detail || routeOwnsIdentityDenial) {
    return null;
  }

  // D-T42, OD-27: requesting more access can't fix a signed-in-as refusal, so
  // that modal drops the access guidance and only says to sign in as yourself.
  const signedInAsRefusal =
    modalState.detail.errorCode === "SPOOF_ACCESS_FORBIDDEN";
  const permissionsContent =
    requiredPermissions.length > 0 ? (
      <div className="vnext-chip-list">
        {requiredPermissions.map((permissionKey) => (
          <VNextPill key={permissionKey} tone="warning">
            {formatAdminPermissionLabel(permissionKey)}
          </VNextPill>
        ))}
      </div>
    ) : (
      <p className="vnext-muted">
        {adminUiCopy.authAccess.noCurrentRoleAccess}
      </p>
    );

  const sanitizedReason = modalState.detail.reason
    ? sanitizeAdminPermissionCopy(modalState.detail.reason)
    : null;

  return (
    <VNextDialog
      footer={
        <>
          {signedInAsRefusal ? null : (
            <Link
              className="vnext-button vnext-button--secondary"
              to="/settings/approvals"
            >
              {adminUiCopy.common.openApprovalsQueue}
            </Link>
          )}
          <Link className="vnext-button vnext-button--quiet" to="/dashboard">
            {adminUiCopy.common.openDashboard}
          </Link>
          <button
            className="vnext-button vnext-button--primary"
            onClick={closeModal}
            type="button"
          >
            {adminUiCopy.common.close}
          </button>
        </>
      }
      onClose={closeModal}
      open={modalState.isOpen}
      title={
        signedInAsRefusal
          ? adminUiCopy.authAccess.signedInAsModalTitle
          : adminUiCopy.authAccess.modalTitle
      }
    >
      <div className="vnext-card__body">
        {signedInAsRefusal ? (
          <p className="vnext-muted">
            {adminUiCopy.authAccess.signedInAsSessionCannotOpen}
          </p>
        ) : (
          <>
            <VNextNotice tone="warning">
              {adminUiCopy.authAccess.modalNotice}
            </VNextNotice>

            <p className="vnext-muted">
              {adminUiCopy.authAccess.modalHelp} Use{" "}
              <strong>{adminUiCopy.common.openApprovalsQueue}</strong> to follow
              up on this area.
            </p>
          </>
        )}

        {sanitizedReason ? (
          <p className="vnext-muted">{sanitizedReason}</p>
        ) : null}

        {signedInAsRefusal ? null : (
          <section className="vnext-card__body">
            <h3 className="vnext-card__title">
              {adminUiCopy.authAccess.accessNeededTitle}
            </h3>
            {permissionsContent}
          </section>
        )}
      </div>
    </VNextDialog>
  );
};

export default AdminAccessDeniedModal;
